import { useEffect, useLayoutEffect, useRef, useState, useCallback, type CSSProperties } from 'react';
import { ChevronDown, Users, User, X } from 'lucide-react';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type { DesignerPath, OrgPathNode } from '../types';
import { useOrgNodeRoster } from '../hooks/useOrgNodeRoster';
import { useWorkspaceRoster, type WorkspaceRosterUser } from '../hooks/useWorkspaceRoster';
import {
  assigneesOnDesign,
  buildOrgById,
  canAddToPath,
  pathParentOf,
  getPathsForNode,
  ownerAbove,
  ownerOfUnit,
  parentInAnyPath,
} from '../lib/pathBuilder';

interface OrgPathCanvasProps {
  orgNodes: OrgPathNode[];
  paths: DesignerPath[];
  activePathId: string;
  maxRelativeLevel: number;
  anchorId: string;
  onToggleNode: (nodeId: string) => void;
  onAssignNode: (nodeId: string, ref: CatchballParticipantRef | null) => void;
  /** The cycle's owner: L0 names them, and a unit with no owner above it falls back to them. */
  cycleOwner: CatchballParticipantRef | null;
  /** A click that would branch the selected path, waiting for an answer. */
  pendingFork: { nodeId: string; pathId: string } | null;
  onConfirmFork: () => void;
  onForkAsNewPath: () => void;
  onCancelFork: () => void;
}

export function OrgPathCanvas({
  orgNodes,
  paths,
  activePathId,
  maxRelativeLevel,
  anchorId,
  onToggleNode,
  onAssignNode,
  cycleOwner,
  pendingFork,
  onConfirmFork,
  onForkAsNewPath,
  onCancelFork,
}: OrgPathCanvasProps) {
  const orgById = buildOrgById(orgNodes);
  const activePath = paths.find((p) => p.id === activePathId);
  const roster = useOrgNodeRoster(orgNodes);
  // Anyone in the workspace can own a unit: offering only the people placed in
  // it left "Assign owner" empty wherever nobody had been placed yet.
  const workspace = useWorkspaceRoster(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [connectorPaths, setConnectorPaths] = useState<Array<{ key: string; d: string; color: string; dashed: boolean }>>([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  // Read through a ref so a new orgNodes array does not re-create the callback.
  const orgNodesRef = useRef(orgNodes);
  orgNodesRef.current = orgNodes;

  const recomputeConnectors = useCallback(() => {
    if (!contentRef.current) return;
    const byId = buildOrgById(orgNodesRef.current);
    const nextSize = {
      width: contentRef.current.offsetWidth,
      height: contentRef.current.offsetHeight,
    };
    setSvgSize((prev) =>
      prev.width === nextSize.width && prev.height === nextSize.height ? prev : nextSize,
    );
    const cRect = contentRef.current.getBoundingClientRect();
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    const segments: Array<{ key: string; d: string; color: string; dashed: boolean }> = [];
    for (const path of paths) {
      // Each stop is drawn from the stop above it on the path, so a path that
      // forks shows one line per branch instead of a zig-zag through the list.
      for (let i = 1; i < path.nodes.length; i++) {
        const fromId = pathParentOf(path, path.nodes[i], byId);
        if (!fromId) continue;
        const fromEl = document.getElementById(`node-${fromId}`);
        const toEl = document.getElementById(`node-${path.nodes[i]}`);
        if (!fromEl || !toEl) continue;
        const fRect = fromEl.getBoundingClientRect();
        const tRect = toEl.getBoundingClientRect();
        const fx = fRect.left - cRect.left + fRect.width / 2;
        const fy = fRect.top - cRect.top + fRect.height + scrollTop;
        const tx = tRect.left - cRect.left + tRect.width / 2;
        const ty = tRect.top - cRect.top + scrollTop;
        const midY = (fy + ty) / 2;
        segments.push({
          key: `${path.id}-${i}`,
          d: `M ${fx} ${fy} C ${fx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`,
          color: path.color,
          dashed: fromId !== anchorId,
        });
      }
    }
    setConnectorPaths((prev) => {
      if (
        prev.length === segments.length &&
        prev.every(
          (seg, i) =>
            seg.key === segments[i]?.key &&
            seg.d === segments[i]?.d &&
            seg.color === segments[i]?.color &&
            seg.dashed === segments[i]?.dashed,
        )
      ) {
        return prev;
      }
      return segments;
    });
  }, [paths, anchorId]);

  useLayoutEffect(() => {
    recomputeConnectors();
  }, [recomputeConnectors]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener('scroll', recomputeConnectors);
    window.addEventListener('resize', recomputeConnectors);
    const ro = new ResizeObserver(recomputeConnectors);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => {
      scrollEl?.removeEventListener('scroll', recomputeConnectors);
      window.removeEventListener('resize', recomputeConnectors);
      ro.disconnect();
    };
  }, [recomputeConnectors]);

  const levels = Array.from({ length: maxRelativeLevel }, (_, i) => i + 1);
  const anchor = orgById.get(anchorId);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      <div
        style={{
          minHeight: 40,
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '6px 14px',
          gap: 10,
          background: 'var(--bg-raised)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '0.68rem',
            letterSpacing: '0.12em',
            color: 'var(--text-ter)',
            textTransform: 'uppercase',
          }}
        >
          Editing
        </span>
        {activePath ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-pri)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: activePath.color }} />
            {activePath.name}
          </span>
        ) : (
          <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)' }}>No path selected</span>
        )}
        <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
          Click a unit to add it to this path or take it off. Pick another path on the left to edit it.
        </span>
      </div>

      {pendingFork && activePath && (
        <ForkQuestion
          pathName={paths.find((p) => p.id === pendingFork.pathId)?.name ?? activePath.name}
          unitName={orgById.get(pendingFork.nodeId)?.name ?? 'this unit'}
          alreadyTo={pathChildrenNames(paths.find((p) => p.id === pendingFork.pathId), orgById.get(pendingFork.nodeId)?.parentId ?? null, orgById)}
          onBranch={onConfirmFork}
          onNewPath={onForkAsNewPath}
          onCancel={onCancelFork}
        />
      )}

      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: '20px 16px', position: 'relative' }}>
        <div ref={contentRef} style={{ position: 'relative', minWidth: 'min-content' }}>
          {anchor && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                  L0 · Anchor
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <NodeCard
                  node={anchor}
                  paths={paths}
                  activePath={activePath}
                  orgById={orgById}
                  onToggle={onToggleNode}
                  isAnchor
                  roster={roster}
                  workspaceUsers={workspace.users}
                  onAssignNode={onAssignNode}
                  cycleOwner={cycleOwner}
                />
              </div>
            </div>
          )}

          {levels.map((level) => {
            const atLevel = orgNodes.filter((n) => n.relativeLevel === level);
            if (atLevel.length === 0) return null;
            return (
              <div key={level} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                    L{level}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'nowrap', minWidth: 'min-content' }}>
                  {atLevel.map((node) => (
                    <NodeCard
                      key={node.id}
                      node={node}
                      paths={paths}
                      activePath={activePath}
                      orgById={orgById}
                      onToggle={onToggleNode}
                      roster={roster}
                      workspaceUsers={workspace.users}
                      onAssignNode={onAssignNode}
                      cycleOwner={cycleOwner}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 1,
            }}
            width={svgSize.width}
            height={svgSize.height}
          >
            {connectorPaths.map((seg) => (
              <path
                key={seg.key}
                d={seg.d}
                stroke={seg.color}
                strokeWidth={1.5}
                fill="none"
                opacity={0.6}
                strokeDasharray={seg.dashed ? '4 2' : undefined}
              />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

function NodeCard({
  node,
  paths,
  activePath,
  orgById,
  onToggle,
  isAnchor,
  roster,
  workspaceUsers,
  onAssignNode,
  cycleOwner,
}: {
  node: OrgPathNode;
  paths: DesignerPath[];
  activePath: DesignerPath | undefined;
  orgById: Map<string, OrgPathNode>;
  onToggle: (id: string) => void;
  isAnchor?: boolean;
  roster: ReturnType<typeof useOrgNodeRoster>;
  workspaceUsers: WorkspaceRosterUser[];
  onAssignNode: (nodeId: string, ref: CatchballParticipantRef | null) => void;
  cycleOwner: CatchballParticipantRef | null;
}) {
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const nodePaths = getPathsForNode(node.id, paths);
  const inActivePath = activePath?.nodes.includes(node.id);
  const { can, reason } = canAddToPath(node.id, activePath, orgById);
  const softUnavailable = !isAnchor && !parentInAnyPath(node.id, paths, orgById);
  const pathColorClass = inActivePath && activePath ? activePath.color : nodePaths[0]?.color;

  const borderColor = inActivePath
    ? pathColorClass
    : nodePaths.length > 0
      ? nodePaths[0].color
      : 'var(--rule-bright)';

  const bg =
    inActivePath && activePath
      ? `${activePath.color}18`
      : nodePaths.length > 0
        ? `${nodePaths[0].color}10`
        : 'var(--surface)';

  return (
    <div
      id={`node-${node.id}`}
      role="button"
      // The reason string already knows what a click will do ("Add to path —
      // forks below Product", "Add Quality to path first"). Surfacing it on hover is the
      // difference between a considered click and a stop vanishing.
      title={isAnchor ? undefined : reason || undefined}
      tabIndex={isAnchor ? -1 : 0}
      onClick={() => !isAnchor && can && onToggle(node.id)}
      onKeyDown={(e) => {
        if (!isAnchor && can && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onToggle(node.id);
        }
      }}
      style={{
        minWidth: 140,
        padding: '10px 12px',
        border: `1px solid ${borderColor}`,
        background: bg,
        opacity: softUnavailable ? 0.35 : 1,
        cursor: isAnchor || !can ? 'default' : 'pointer',
        position: 'relative',
        // Elevate above sibling cards while the assignee menu is open — those
        // cards share z-index 2 and paint later, so their translucent fills
        // otherwise sit on top of the dropdown (text-on-text bleed).
        zIndex: assigneeMenuOpen ? 40 : 2,
      }}
    >
      <div style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text-pri)' }}>{node.name}</div>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-ter)', marginTop: 2 }}>{node.title}</div>
      {nodePaths.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
          {nodePaths.map((p) => (
            <span
              key={p.id}
              title={p.name}
              style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }}
            />
          ))}
        </div>
      )}
      {isAnchor ? (
        <CycleOwnerLine owner={cycleOwner} />
      ) : nodePaths.length > 0 ? (
        // Every unit on any path shows its owner, whichever path is selected:
        // the owner belongs to the unit for the whole cycle.
        <NodeAssigneeControl
          assignment={ownerOfUnit(node.id, paths) ?? undefined}
          fallback={ownerAbove(node.id, paths, orgById, cycleOwner)}
          fallbackFromName={(id) => orgById.get(id)?.name ?? 'the unit above'}
          users={roster.usersAtNode(node.id)}
          groups={roster.groupsAtNode(node.id)}
          alsoOffer={assigneesOnDesign(paths)}
          workspaceUsers={workspaceUsers}
          loading={roster.loading}
          onAssign={(ref) => onAssignNode(node.id, ref)}
          onOpenChange={setAssigneeMenuOpen}
        />
      ) : null}
    </div>
  );
}

/**
 * A unit's owner: the user or group who answers for it and receives the ball
 * there. With none named, the owner above answers for it (owner ruling
 * 2026-11-03), and the control says who that is, so an empty unit never reads
 * as nobody.
 */
function NodeAssigneeControl({
  assignment,
  fallback,
  fallbackFromName,
  users,
  workspaceUsers,
  groups,
  alsoOffer,
  loading,
  onAssign,
  onOpenChange,
}: {
  assignment: CatchballParticipantRef | undefined;
  fallback: { ref: CatchballParticipantRef; from: string | null } | null;
  fallbackFromName: (unitId: string) => string;
  users: { id: string; name: string; email: string }[];
  groups: { id: string; name: string }[];
  alsoOffer: CatchballParticipantRef[];
  workspaceUsers: WorkspaceRosterUser[];
  loading: boolean;
  onAssign: (ref: CatchballParticipantRef | null) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const extraUsers = alsoOffer.filter(
    (r) => r.type === 'user' && !users.some((u) => u.id === r.id),
  );
  const extraGroups = alsoOffer.filter(
    (r) => r.type === 'group' && !groups.some((g) => g.id === r.id),
  );
  const everyoneElse = workspaceUsers.filter(
    (u) => !users.some((x) => x.id === u.id) && !extraUsers.some((x) => x.id === u.id),
  );
  const hasOptions =
    users.length > 0 || groups.length > 0 || extraUsers.length > 0 || extraGroups.length > 0 || everyoneElse.length > 0;

  const setMenuOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--rule)', position: 'relative' }}
    >
      {assignment ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {assignment.type === 'group' ? (
            <Users size={11} style={{ color: 'var(--text-ter)', flexShrink: 0 }} />
          ) : (
            <User size={11} style={{ color: 'var(--text-ter)', flexShrink: 0 }} />
          )}
          <span
            style={{
              fontSize: '0.72rem',
              color: 'var(--text-sec)',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {assignment.displayName}
          </span>
          <button
            type="button"
            onClick={() => onAssign(null)}
            title="Clear — the owner above answers for this unit"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0, display: 'flex' }}
          >
            <X size={11} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setMenuOpen(!open)}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: loading ? 'default' : 'pointer',
            fontSize: '0.72rem',
            color: 'var(--text-dim)',
          }}
        >
          <span style={{ flex: 1, textAlign: 'left', fontWeight: 600, color: 'var(--mustard)' }}>
            {loading ? 'Loading…' : 'Assign owner'}
          </span>
          {!loading && <ChevronDown size={11} />}
        </button>
      )}
      {!assignment && !loading && (
        <div style={{ marginTop: 3, fontSize: '0.68rem', lineHeight: 1.3, color: 'var(--text-dim)' }}>
          {fallback
            ? `Until then ${fallback.ref.displayName} answers for it (${fallback.from ? `owner of ${fallbackFromName(fallback.from)}` : 'cycle owner'}).`
            : 'Nobody answers for it yet: the cycle has no owner.'}
        </div>
      )}

      {open && !assignment && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            width: 220,
            maxHeight: 220,
            overflowY: 'auto',
            // Solid surface — not the path-tinted card fill — so menu rows stay readable
            background: 'var(--surface)',
            border: '1px solid var(--rule-bright)',
            borderRadius: 2,
            zIndex: 50,
            boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
            isolation: 'isolate',
          }}
        >
          {!hasOptions ? (
            <div style={{ padding: '10px 12px', fontSize: '0.72rem', color: 'var(--text-dim)' }}>
              Nobody in this workspace to name yet.
            </div>
          ) : (
            <>
              {users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    onAssign({ type: 'user', id: u.id, displayName: u.name || u.email });
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: 'none',
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{u.name || u.email}</span>
                  {u.name && u.name !== u.email && <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>{u.email}</span>}
                </button>
              ))}
              {extraUsers.map((r) => (
                <button
                  key={`extra-${r.id}`}
                  type="button"
                  onClick={() => {
                    onAssign(r);
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: 'none',
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{r.displayName}</span>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>On another path</span>
                </button>
              ))}
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    onAssign({ type: 'group', id: g.id, displayName: g.name });
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: 'none',
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                  }}
                >
                  <Users size={11} style={{ color: 'var(--mustard)', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{g.name}</span>
                </button>
              ))}
              {extraGroups.map((r) => (
                <button
                  key={`extra-g-${r.id}`}
                  type="button"
                  onClick={() => {
                    onAssign(r);
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: 'none',
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                  }}
                >
                  <Users size={11} style={{ color: 'var(--mustard)', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{r.displayName}</span>
                </button>
              ))}
              {everyoneElse.length > 0 && (
                <div style={{ padding: '6px 10px 4px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                  {users.length + extraUsers.length + groups.length + extraGroups.length > 0 ? 'Anyone else in the workspace' : 'Anyone in the workspace'}
                </div>
              )}
              {everyoneElse.map((u) => (
                <button
                  key={`ws-${u.id}`}
                  type="button"
                  onClick={() => {
                    onAssign({ type: 'user', id: u.id, displayName: u.name || u.email });
                    setMenuOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: 'none',
                    borderBottom: '1px solid var(--rule)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-pri)' }}>{u.name || u.email}</span>
                  {u.name && u.name !== u.email && <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>{u.email}</span>}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** L0 names the cycle's owner, the one owner every cycle must have (canon 03 §2.1). */
function CycleOwnerLine({ owner }: { owner: CatchballParticipantRef | null }) {
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--rule)', fontSize: '0.72rem' }}>
      {owner ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-sec)' }}>
          <User size={11} style={{ color: 'var(--text-ter)', flexShrink: 0 }} />
          {owner.displayName}
          <span style={{ color: 'var(--text-dim)' }}>· cycle owner</span>
        </span>
      ) : (
        <span style={{ fontWeight: 600, color: 'var(--coral)' }}>
          This cycle has no owner. Name one in the cycle's settings before launch.
        </span>
      )}
    </div>
  );
}

/** The stops a path already sends to below `parentId`, by name. */
function pathChildrenNames(
  path: DesignerPath | undefined,
  parentId: string | null,
  orgById: Map<string, OrgPathNode>,
): string[] {
  if (!path || !parentId) return [];
  return path.nodes.filter((n) => orgById.get(n)?.parentId === parentId).map((n) => orgById.get(n)?.name ?? n);
}

function ForkQuestion({
  pathName,
  unitName,
  alreadyTo,
  onBranch,
  onNewPath,
  onCancel,
}: {
  pathName: string;
  unitName: string;
  alreadyTo: string[];
  onBranch: () => void;
  onNewPath: () => void;
  onCancel: () => void;
}) {
  const btn = (primary: boolean): CSSProperties => ({
    padding: '5px 10px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    border: '1px solid var(--rule-bright)',
    background: primary ? 'var(--text-pri)' : 'var(--bg)',
    color: primary ? 'var(--bg)' : 'var(--text-sec)',
  });
  return (
    <div
      role="alertdialog"
      aria-label={`Add ${unitName}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--rule)',
        background: 'var(--mustard-dim, var(--bg-raised))',
      }}
    >
      <span style={{ fontSize: '0.88rem', color: 'var(--text-pri)', flex: '1 1 320px' }}>
        {pathName} already goes to {alreadyTo.join(' and ') || 'another unit'} at this level. Add {unitName} as a second branch of {pathName}, or start a new path to {unitName}?
      </span>
      <button type="button" style={btn(true)} onClick={onNewPath}>New path to {unitName}</button>
      <button type="button" style={btn(false)} onClick={onBranch}>Branch {pathName}</button>
      <button type="button" style={{ ...btn(false), border: 'none', background: 'none' }} onClick={onCancel}>Cancel</button>
    </div>
  );
}
